// src/shine/components/product-feeds/product-feed-general-settings/product-feed-general-settings.component.ts

import {Component, inject, Input, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';

import {MaterialModule} from '../../../../material.module';
import {ToastService} from '../../../../services/layout/toast.service';
import {IProductFeedOptions, IProductFeedRouteRule} from '../../../../models/product-feed-options.model';

interface RoutingPreview {
    status: 'idle' | 'invalid' | 'no-match' | 'match' | 'ambiguous';
    summary: string;
}

@Component({
  selector: 'app-product-feed-general-settings',
  templateUrl: './product-feed-general-settings.component.html',
  styleUrls: ['./product-feed-general-settings.component.scss'],
    imports: [MaterialModule, ReactiveFormsModule],
  standalone: true
})
export class ProductFeedGeneralSettingsComponent implements OnInit {
    private snackBar = inject(ToastService);
    private fb = inject(FormBuilder);
    private readonly maxProgressTextLength = 160;

  @Input() settingsForm!: FormGroup;
  @Input() productFeedId = '';
  @Input() availableSourceFeeds: IProductFeedOptions[] = [];

  crawlTypes: string[] = ['Url', 'Sitemap', 'Xml', 'Json'];

  // Lucene analyzers
  analyzers: string[] = ['NorwegianAnalyzer', 'SwedishAnalyzer'];
  types: string[] = ['Normal', 'Selenium'];
  readonly routePreviewUrl = new FormControl('', {nonNullable: true});

  ngOnInit(): void {
    if (!this.settingsForm) {
      throw new Error('settingsForm Input is required for ProductFeedGeneralSettingsComponent');
    }
  }

  get routeRules(): FormArray {
    return this.settingsForm.get('routeRules') as FormArray;
  }

  get detailHostAllowlist(): FormArray {
    return this.settingsForm.get('detailHostAllowlist') as FormArray;
  }

  get isAssociatedDetailFeed(): boolean {
    return !!String(this.settingsForm.get('sourceFeedId')?.value ?? '').trim();
  }

  onSourceFeedChange(sourceFeedId: string): void {
    if (String(sourceFeedId ?? '').trim()) {
      this.settingsForm.patchValue({
        type: 'Json',
        skipForPageCollection: true
      });
    }
    this.settingsForm.markAsTouched();
    this.settingsForm.updateValueAndValidity();
  }

  get productSearchProgressTextControl(): AbstractControl | null {
    return this.settingsForm.get('productSearchProgressText');
  }

  addRouteRule(): void {
    this.routeRules.push(this.fb.group({
      domainPattern: [''],
      pathPattern: ['*'],
      priority: [0]
    }));
    this.routeRules.markAsTouched();
  }

  removeRouteRule(index: number): void {
    this.routeRules.removeAt(index);
    this.routeRules.markAsTouched();
  }

  get routingPreview(): RoutingPreview {
      const value = this.routePreviewUrl.value.trim();
      if (!value) {
          return {status: 'idle', summary: 'Enter an absolute page URL to preview this feed’s route rules.'};
      }

      let url: URL;
      try {
          url = new URL(value);
          if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Unsupported protocol');
      } catch {
          return {status: 'invalid', summary: 'Enter an absolute http or https URL.'};
      }

      const rules = this.routeRules.getRawValue() as IProductFeedRouteRule[];
      const matches = rules
          .map((rule, index) => ({rule, index, score: this.routeRuleScore(rule)}))
          .filter(candidate => this.routeRuleMatches(candidate.rule, url))
          .sort((left, right) => right.score - left.score);

      if (matches.length === 0) {
          return {
              status: 'no-match',
              summary: rules.length > 0
                  ? 'No rule matches. When candidate feeds have route rules, runtime fails closed for this URL.'
                  : 'No route rules are configured; runtime falls back to requested market, then Default feed.'
          };
      }

      const bestScore = matches[0].score;
      const best = matches.filter(match => match.score === bestScore);
      if (best.length > 1) {
          return {
              status: 'ambiguous',
              summary: `${best.length} rules on this feed tie at score ${bestScore}. Resolve the tie with priority or a more specific pattern. Final feed selection also depends on other candidate feeds.`
          };
      }

      return {
          status: 'match',
          summary: `Rule ${best[0].index + 1} is the best rule on this feed with score ${bestScore}. Final feed selection also depends on other candidate feeds.`
      };
  }

  addDetailHost(): void {
    this.detailHostAllowlist.push(this.createDetailHostControl());
    this.detailHostAllowlist.markAsTouched();
    this.settingsForm.updateValueAndValidity();
  }

  removeDetailHost(index: number): void {
    this.detailHostAllowlist.removeAt(index);
    this.detailHostAllowlist.markAsTouched();
    this.settingsForm.updateValueAndValidity();
  }

  detailHostError(index: number): string {
    const control = this.detailHostAllowlist.at(index);
    if (control.hasError('required')) return 'Host is required.';
    if (control.hasError('invalidHost')) return 'Enter a hostname with an optional port, without scheme, path, wildcard, or IP address.';
    return '';
  }

    resetProductFeedOptions(): void {
        if (!this.settingsForm) {
            return;
        }

        const defaultValues = {
            id: '',
            name: '',
            type: '',
            skipForPageCollection: false,
            sourceFeedId: '',
            detailHostAllowlist: [],
            url: '',
            listingsDirectory: '',
            stripNamespace: '',
            urlProperty: '',
            crawlRegexPattern: '',
            urlRegexPattern: '',
            urlRegexReplacePattern: '',
            customCategorySyntax: '',
            defaultDomain: '',
            brandDomain: '',
            market: '',
            productSearchProgressText: '',
            isDefaultFeed: false,
            routeRules: [],
            crawlFrequencyHours: 24,
            lastFetchDateTime: '',
            fieldMapping: {},
            filters: [],
            enabled: false
        };

        this.routeRules.clear();
        this.detailHostAllowlist.clear();
        this.settingsForm.reset(defaultValues);
        this.snackBar.showSuccess('Product feed settings have been reset.', 3000);
    }

    private createDetailHostControl(): AbstractControl {
        return this.fb.control('', [control => {
            const value = String(control.value ?? '').trim();
            if (!value) return {required: true};
            if (!this.isValidDetailHost(value)) {
                return {invalidHost: true};
            }
            return null;
        }]);
    }

    private isValidDetailHost(value: string): boolean {
        if (/[*/?#@]/.test(value)) return false;

        try {
            const url = new URL(`https://${value}`);
            const host = url.hostname.replace(/^\[|\]$/g, '');
            return !!host && !this.isIpAddress(host);
        } catch {
            return false;
        }
    }

    private isIpAddress(host: string): boolean {
        return /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host) || /^[0-9a-f:]+$/i.test(host);
    }

    private routeRuleMatches(rule: IProductFeedRouteRule, url: URL): boolean {
        const host = this.normalizeHost(url.hostname);
        const domainPattern = this.normalizeHost(rule.domainPattern);
        const pathPattern = String(rule.pathPattern ?? '').trim() || '*';
        const domainMatches = !domainPattern || domainPattern === '*'
            ? true
            : domainPattern.includes('*')
                ? this.wildcardMatches(host, domainPattern)
                : host === domainPattern || host.endsWith(`.${domainPattern}`);

        return domainMatches && this.wildcardMatches(url.pathname || '/', pathPattern);
    }

    private routeRuleScore(rule: IProductFeedRouteRule): number {
        return (Number(rule.priority) || 0) * 1000 +
            this.patternSpecificity(rule.domainPattern) +
            this.patternSpecificity(rule.pathPattern);
    }

    private patternSpecificity(pattern: string | null | undefined): number {
        const value = String(pattern ?? '').trim();
        return !value || value === '*' ? 0 : [...value].filter(character => character !== '*').length;
    }

    private wildcardMatches(value: string, pattern: string): boolean {
        const normalizedPattern = String(pattern ?? '').trim();
        if (!normalizedPattern || normalizedPattern === '*') return true;
        const escaped = normalizedPattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\\*/g, '.*');
        return new RegExp(`^${escaped}$`, 'i').test(value);
    }

    private normalizeHost(value: string | null | undefined): string {
        let host = String(value ?? '').trim().replace(/\/$/, '').toLowerCase();
        try {
            host = new URL(host).hostname;
        } catch {
            // Host-only patterns are expected and do not need URL parsing.
        }
        return host.startsWith('www.') ? host.slice(4) : host;
    }

    get productSearchProgressText(): string {
        return String(this.settingsForm.get('productSearchProgressText')?.value ?? '');
    }

    get productSearchProgressTextLength(): number {
        return this.productSearchProgressText.length;
    }

    get productSearchProgressTextPreview(): string {
        return this.productSearchProgressText.trim();
    }

    get maxProgressTextLengthLeft(): number {
        return this.maxProgressTextLength - this.productSearchProgressTextLength;
    }

    get productSearchProgressTextHasValidationError(): boolean {
        const control = this.productSearchProgressTextControl;
        return !!(control?.invalid && (control.dirty || control.touched));
    }

    get productSearchProgressTextValidationError(): string {
        const control = this.productSearchProgressTextControl;
        if (!control?.errors) return '';

        if (control.errors['productSearchProgressTextTooLong']) return 'The progress text must be 160 characters or less.';
        if (control.errors['productSearchProgressTextContainsMarkup']) return 'Only plain text is allowed.';
        if (control.errors['productSearchProgressTextContainsControlCharacters']) return 'Control characters are not allowed.';

        return 'Invalid progress text.';
    }
}
